import { useEffect, useState } from "react";
import type { DashboardCounts, Partner } from "../types";
import { useData } from "../hooks/useData";
import { api } from "../api/client";
import { I } from "../components/Icons";
import { clsx } from "../components/Primitives";
import { currentSubscription, isStandalone, pushSupported, subscribeThisDevice, unsubscribeThisDevice } from "./push";

/** Everything that isn't on a tab, as one list. */
export default function PhoneMore({ go, me, counts, onLogout }: {
  go: (s: string, id?: string | null) => void; me: Partner; counts: DashboardCounts | null; onLogout: () => void;
}) {
  const items: { id: string; label: string; sub: string; icon: (p: { size?: number }) => React.ReactNode; n?: number }[] = [
    { id: "contacts", label: "Contacts", sub: "Call, WhatsApp, email, minutes", icon: I.phone },
    { id: "clients", label: "Accounts", sub: "Companies and who we know there", icon: I.meeting },
    { id: "pipeline", label: "Pipeline", sub: "Deals by stage", icon: I.pipeline },
    { id: "radar", label: "Radar", sub: "Stories and signals to act on", icon: I.radar, n: counts?.radar },
    { id: "leads", label: "Leads", sub: "Sign-ups from the websites", icon: I.bell, n: counts?.leads },
    { id: "newsletters", label: "Newsletters", sub: "Review and approve", icon: I.send },
    { id: "web", label: "Web", sub: "Visitors, Google searches, markets", icon: I.globe },
    { id: "targets", label: "Relationship targets", sub: "Who we want to know", icon: I.signal },
    { id: "files", label: "Files", sub: "Drive folders per deal", icon: I.doc },
    ...(me.is_admin ? [{ id: "settings", label: "Settings", sub: "Team, bots, markets, info site", icon: I.sliders }] : []),
  ];
  return (
    <div className="ph-pad">
      <div className="ph-card">
        {items.map(it => (
          <button key={it.id} className="ph-row ph-row-btn" onClick={() => go(it.id)}>
            <span className="ph-av ic">{it.icon({ size: 18 })}</span>
            <span className="ph-row-m"><span className="ph-row-t">{it.label}</span><span className="ph-row-s">{it.sub}</span></span>
            {!!it.n && <span className="ph-chip flat">{it.n}</span>}
            <span className="ph-due">›</span>
          </button>
        ))}
      </div>
      <h2 className="ph-h2">This phone</h2>
      <div className="ph-card">
        <button className="ph-row ph-row-btn" onClick={() => go("notifications")}>
          <span className="ph-av ic">{I.bell({ size: 18 })}</span>
          <span className="ph-row-m"><span className="ph-row-t">Notifications</span><span className="ph-row-s">Replies, hand-overs, morning summary</span></span>
          <span className="ph-due">›</span>
        </button>
        <button className="ph-row ph-row-btn" onClick={() => go("install")}>
          <span className="ph-av ic">{I.home({ size: 18 })}</span>
          <span className="ph-row-m"><span className="ph-row-t">{isStandalone() ? "Installed on the Home Screen" : "Add to Home Screen"}</span><span className="ph-row-s">And “Send to Saga” from any app</span></span>
          <span className="ph-due">›</span>
        </button>
        <button className="ph-row ph-row-btn" onClick={() => { window.location.href = "/?desktop=1"; }}>
          <span className="ph-av ic">{I.ext({ size: 18 })}</span>
          <span className="ph-row-m"><span className="ph-row-t">Full desktop layout</span><span className="ph-row-s">The same CRM as on a computer</span></span>
        </button>
      </div>
      <div className="ph-me">Signed in as <b>{me.name}</b> <button className="ph-link" onClick={onLogout}>Sign out</button></div>
    </div>
  );
}

interface PushInfo {
  enabled: boolean; public_key: string | null; devices: { id: number; user_agent: string; created_at: string }[];
  prefs: { replies: boolean; handoffs: boolean; morning: boolean; quiet_from: string; quiet_to: string; tz: string };
}

export function PhoneNotifications() {
  const { data, refetch } = useData<PushInfo>("/api/phone/push");
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { currentSubscription().then(s => setOn(!!s)).catch(() => setOn(false)); }, []);
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true); setMsg(null);
    try { await fn(); if (ok) setMsg(ok); refetch(); }
    catch (e) { setMsg(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const save = (patch: Partial<PushInfo["prefs"]>) => run(() => api.put("/api/phone/push/prefs", patch));

  if (!data) return <div className="ph-pad"><div className="ph-skel" /></div>;
  const p = data.prefs;
  return (
    <div className="ph-pad">
      {!data.enabled && <div className="ph-note">Notifications aren't switched on for the CRM yet (the server needs its VAPID keys — see DEPLOYMENT.md). Your choices below are kept.</div>}
      {data.enabled && !isStandalone() && <div className="ph-note">On iPhone, notifications only work in the app on your Home Screen. Open Saga from the Home Screen and come back here.</div>}
      {msg && <div className="ph-note">{msg}</div>}
      <div className="ph-card">
        <div className="ph-row">
          <span className="ph-row-m"><span className="ph-row-t">This phone</span><span className="ph-row-s">{on ? "Receives notifications" : "Doesn't receive notifications"}</span></span>
          {on
            ? <button className="ph-chip" disabled={busy} onClick={() => run(async () => { await unsubscribeThisDevice(); setOn(false); }, "This phone won't get notifications")}>Turn off</button>
            : <button className="ph-chip ok" disabled={busy || !data.enabled || !data.public_key || !pushSupported()}
                onClick={() => run(async () => { await subscribeThisDevice(data.public_key!); setOn(true); }, "Done — try “Send a test”")}>Turn on</button>}
        </div>
        {on && <div className="ph-row"><span className="ph-row-m"><span className="ph-row-t">Send a test</span></span>
          <button className="ph-chip" disabled={busy} onClick={() => run(() => api.post("/api/phone/push/test", {}), "Test sent")}>Send</button></div>}
      </div>
      <h2 className="ph-h2">Tell me when</h2>
      <div className="ph-card">
        <Toggle label="A reply arrives on a task I'm waiting on" on={p.replies} onChange={v => save({ replies: v })} />
        <Toggle label="Someone hands me a task" on={p.handoffs} onChange={v => save({ handoffs: v })} />
        <Toggle label="Morning summary at 7" on={p.morning} onChange={v => save({ morning: v })} />
      </div>
      <h2 className="ph-h2">Quiet hours</h2>
      <div className="ph-card">
        <div className="ph-row"><span className="ph-row-m"><span className="ph-row-t">From</span></span>
          <input className="ph-time" type="time" value={p.quiet_from} onChange={e => save({ quiet_from: e.target.value })} /></div>
        <div className="ph-row"><span className="ph-row-m"><span className="ph-row-t">Until</span></span>
          <input className="ph-time" type="time" value={p.quiet_to} onChange={e => save({ quiet_to: e.target.value })} /></div>
        <div className="ph-row"><span className="ph-row-m"><span className="ph-row-t">Time zone</span><span className="ph-row-s">{p.tz}</span></span>
          {Intl.DateTimeFormat().resolvedOptions().timeZone !== p.tz &&
            <button className="ph-chip" onClick={() => save({ tz: Intl.DateTimeFormat().resolvedOptions().timeZone })}>Use this phone's</button>}</div>
      </div>
      <p className="ph-fine">Nothing is pushed during quiet hours; it waits for you in Today.{data.devices.length ? ` ${data.devices.length} device${data.devices.length === 1 ? "" : "s"} registered.` : ""}</p>
    </div>
  );
}

function Toggle({ label, on, onChange }: { label: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="ph-row ph-toggle">
      <span className="ph-row-m"><span className="ph-row-t ph-wrap">{label}</span></span>
      <input type="checkbox" checked={on} onChange={e => onChange(e.target.checked)} />
      <span className={clsx("ph-switch", on && "on")} />
    </label>
  );
}

export function PhoneInstall() {
  return (
    <div className="ph-pad ph-prose">
      <div className="ph-card ph-text">
        <h3>{isStandalone() ? "✓ Saga is on your Home Screen" : "Add Saga to your Home Screen"}</h3>
        <ol>
          <li>Open <b>crm.saga-advisory.com</b> in <b>Safari</b> and sign in.</li>
          <li>Tap the <b>Share</b> button (square with an arrow).</li>
          <li>Choose <b>Add to Home Screen</b>, then <b>Add</b>.</li>
          <li>Open Saga from the new icon. You stay signed in for 30 days of use.</li>
          <li>Go to <b>More → Notifications</b> and tap <b>Turn on</b>.</li>
        </ol>
      </div>
      <div className="ph-card ph-text">
        <h3>“Send to Saga” from any app</h3>
        <p>An iPhone Shortcut that sends whatever you share — an email, a note, a web page — to Saga, which files it under the right deal and suggests tasks.</p>
        <ol>
          <li>Ask an admin for a bot key with the <b>ingest</b> permission (Settings → Bots &amp; agents). Keep it private.</li>
          <li>In <b>Shortcuts</b>, create a new shortcut and turn on <b>Show in Share Sheet</b> (accepts Text, URLs, Safari web pages).</li>
          <li>Add <b>Get Contents of URL</b>: URL <code>https://crm.saga-advisory.com/api/agent/v1/ingest</code>, Method <b>POST</b>.</li>
          <li>Headers: <code>Authorization</code> = <code>Bearer</code> + your key, <code>Content-Type</code> = <code>application/json</code>.</li>
          <li>Request body (JSON): <code>text</code> = <i>Shortcut Input</i>, <code>note</code> = <code>Shared from iPhone</code>.</li>
          <li>Add <b>Show Notification</b> with the result. Name it “Send to Saga”.</li>
        </ol>
        <p className="ph-fine">Full steps in docs/integrations/send-to-saga-shortcut.md.</p>
      </div>
    </div>
  );
}
